import { AnimationEvent, animate, style, transition, trigger } from '@angular/animations';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ViewRef,
  effect,
  signal
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem, OverlayService, PrimeNGConfig, PrimeTemplate, SharedModule } from 'primeng/api';
import { DomHandler } from 'primeng/dom';
import { AngleRightIcon } from 'primeng/icons/angleright';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ObjectUtils, UniqueComponentId, ZIndexUtils } from 'primeng/utils';
import { Nullable, VoidListener } from 'primeng/ts-helpers';

@Component({
  selector: 'p-contextMenuSub',
  template: `
        <ul
            *ngIf="root ? true : visible"
            #sublist
            role="menu"
            [ngClass]="{ 'p-submenu-list': !root, 'p-contextmenu-root-list': root }"
            [@overlayAnimation]="visible"
            (@overlayAnimation.start)="onEnter($event, sublist)"
            [id]="menuId + '_list'"
            [tabindex]="tabindex"
            [attr.aria-label]="ariaLabel"
            [attr.aria-labelledBy]="ariaLabelledBy"
            [attr.aria-activedescendant]="focusedItemId"
            [attr.aria-orientation]="'vertical'"
            [attr.data-pc-section]="'menu'"
            (keydown)="menuKeydown.emit($event)"
            (focus)="menuFocus.emit($event)"
            (blur)="menuBlur.emit($event)"
        >
            <ng-template ngFor let-processedItem [ngForOf]="items" let-index="index">
                <li
                    *ngIf="isItemVisible(processedItem) && getItemProp(processedItem, 'separator')"
                    [id]="getItemId(processedItem)"
                    [style]="getItemProp(processedItem, 'style')"
                    [ngClass]="getSeparatorItemClass(processedItem)"
                    role="separator"
                    [attr.data-pc-section]="'separator'"
                ></li>
                <li
                    #listItem
                    *ngIf="isItemVisible(processedItem) && !getItemProp(processedItem, 'separator')"
                    role="menuitem"
                    [id]="getItemId(processedItem)"
                    [attr.data-pc-section]="'menuitem'"
                    [attr.data-p-highlight]="isItemActive(processedItem)"
                    [attr.data-p-focused]="isItemFocused(processedItem)"
                    [attr.data-p-disabled]="isItemDisabled(processedItem)"
                    [attr.aria-label]="getItemLabel(processedItem)"
                    [attr.aria-disabled]="isItemDisabled(processedItem) || undefined"
                    [attr.aria-haspopup]="isItemGroup(processedItem) && !getItemProp(processedItem, 'to') ? 'menu' : undefined"
                    [attr.aria-expanded]="isItemGroup(processedItem) ? isItemActive(processedItem) : undefined"
                    [attr.aria-level]="level + 1"
                    [attr.aria-setsize]="getAriaSetSize()"
                    [attr.aria-posinset]="getAriaPosInset(index)"
                    [ngStyle]="getItemProp(processedItem, 'style')"
                    [ngClass]="getItemClass(processedItem)"
                    [class]="getItemProp(processedItem, 'styleClass')"
                    pTooltip
                    [tooltipOptions]="getItemProp(processedItem, 'tooltipOptions')"
                >
                    <div [attr.data-pc-section]="'content'" class="p-menuitem-content" (click)="onItemClick($event, processedItem)" (mouseenter)="onItemMouseEnter({$event, processedItem})">
                        <a
                            *ngIf="!getItemProp(processedItem, 'routerLink')"
                            [attr.href]="getItemProp(processedItem, 'url')"
                            [attr.aria-hidden]="true"
                            [attr.data-automationid]="getItemProp(processedItem, 'automationId')"
                            [attr.data-pc-section]="'action'"
                            [target]="getItemProp(processedItem, 'target')"
                            [ngClass]="{ 'p-menuitem-link': true, 'p-disabled': getItemProp(processedItem, 'disabled') }"
                            [attr.tabindex]="-1"
                            pRipple
                        >
                            <span
                                *ngIf="getItemProp(processedItem, 'icon')"
                                class="p-menuitem-icon"
                                [ngClass]="getItemProp(processedItem, 'icon')"
                                [ngStyle]="getItemProp(processedItem, 'iconStyle')"
                                [attr.data-pc-section]="'icon'"
                                [attr.aria-hidden]="true"
                                [attr.tabindex]="-1"
                            >
                            </span>
                            <span *ngIf="getItemProp(processedItem, 'escape'); else htmlLabel" class="p-menuitem-text" [attr.data-pc-section]="'label'">
                                {{ getItemLabel(processedItem) }}
                            </span>
                            <ng-template #htmlLabel>
                                <span class="p-menuitem-text" [innerHTML]="getItemLabel(processedItem)" [attr.data-pc-section]="'label'"></span>
                            </ng-template>
                            <span class="p-menuitem-badge" *ngIf="getItemProp(processedItem, 'badge')" [ngClass]="getItemProp(processedItem, 'badgeStyleClass')">{{ child.badge }}</span>

                            <ng-container *ngIf="isItemGroup(processedItem)">
                                <AngleRightIcon *ngIf="!contextMenu.submenuIconTemplate" [styleClass]="'p-submenu-icon'" [attr.data-pc-section]="'submenuicon'" [attr.aria-hidden]="true" />
                                <ng-template *ngTemplateOutlet="contextMenu.submenuIconTemplate" [attr.data-pc-section]="'submenuicon'" [attr.aria-hidden]="true"></ng-template>
                            </ng-container>
                        </a>
                        <a
                            *ngIf="getItemProp(processedItem, 'routerLink')"
                            [routerLink]="getItemProp(processedItem, 'routerLink')"
                            [attr.data-automationid]="getItemProp(processedItem, 'automationId')"
                            [attr.tabindex]="-1"
                            [attr.aria-hidden]="true"
                            [attr.data-pc-section]="'action'"
                            [queryParams]="getItemProp(processedItem, 'queryParams')"
                            [routerLinkActive]="'p-menuitem-link-active'"
                            [routerLinkActiveOptions]="getItemProp(processedItem, 'routerLinkActiveOptions') || { exact: false }"
                            [target]="getItemProp(processedItem, 'target')"
                            [ngClass]="{ 'p-menuitem-link': true, 'p-disabled': getItemProp(processedItem, 'disabled') }"
                            [fragment]="getItemProp(processedItem, 'fragment')"
                            [queryParamsHandling]="getItemProp(processedItem, 'queryParamsHandling')"
                            [preserveFragment]="getItemProp(processedItem, 'preserveFragment')"
                            [skipLocationChange]="getItemProp(processedItem, 'skipLocationChange')"
                            [replaceUrl]="getItemProp(processedItem, 'replaceUrl')"
                            [state]="getItemProp(processedItem, 'state')"
                            pRipple
                        >
                            <span
                                *ngIf="getItemProp(processedItem, 'icon')"
                                class="p-menuitem-icon"
                                [ngClass]="getItemProp(processedItem, 'icon')"
                                [ngStyle]="getItemProp(processedItem, 'iconStyle')"
                                [attr.data-pc-section]="'icon'"
                                [attr.aria-hidden]="true"
                                [attr.tabindex]="-1"
                            >
                            </span>
                            <span *ngIf="getItemProp(processedItem, 'escape'); else htmlLabel" class="p-menuitem-text" [attr.data-pc-section]="'label'">
                                {{ getItemLabel(processedItem) }}
                            </span>
                            <ng-template #htmlLabel>
                                <span class="p-menuitem-text" [innerHTML]="getItemLabel(processedItem)" [attr.data-pc-section]="'label'"></span>
                            </ng-template>
                            <span class="p-menuitem-badge" *ngIf="getItemProp(processedItem, 'badge')" [ngClass]="getItemProp(processedItem, 'badgeStyleClass')">{{ child.badge }}</span>

                            <ng-container *ngIf="isItemGroup(processedItem)">
                                <AngleRightIcon *ngIf="!contextMenu.submenuIconTemplate" [styleClass]="'p-submenu-icon'" [attr.data-pc-section]="'submenuicon'" [attr.aria-hidden]="true" />
                                <ng-template *ngTemplateOutlet="contextMenu.submenuIconTemplate" [attr.data-pc-section]="'submenuicon'" [attr.aria-hidden]="true"></ng-template>
                            </ng-container>
                        </a>
                    </div>

                    <p-contextMenuSub
                        *ngIf="isItemVisible(processedItem) && isItemGroup(processedItem)"
                        [items]="processedItem.items"
                        [menuId]="menuId"
                        [visible]="isItemActive(processedItem) && isItemGroup(processedItem)"
                        [activeItemPath]="activeItemPath"
                        [focusedItemId]="focusedItemId"
                        [level]="level + 1"
                        (itemClick)="itemClick.emit($event)"
                        (itemMouseEnter)="onItemMouseEnter($event)"
                    ></p-contextMenuSub>
                </li>
            </ng-template>
        </ul>
    `,
  animations: [trigger('overlayAnimation', [transition(':enter', [style({ opacity: 0 })]), transition(':leave', [style({ opacity: 0 })])])],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'p-element'
  }
})
export class ContextMenuSub {
  @Input() visible: boolean = false;

  @Input() items: any[];

  @Input() root: boolean | undefined = false;

  @Input() autoZIndex: boolean = true;

  @Input() baseZIndex: number = 0;

  @Input() popup: boolean | undefined;

  @Input() menuId: string | undefined;

  @Input() ariaLabel: string | undefined;

  @Input() ariaLabelledBy: string | undefined;

  @Input() level: number = 0;

  @Input() focusedItemId: string | undefined;

  @Input() activeItemPath: any[];

  @Input() tabindex: number = 0;

  @Output() itemClick: EventEmitter<any> = new EventEmitter();

  @Output() itemMouseEnter: EventEmitter<any> = new EventEmitter();

  @Output() menuFocus: EventEmitter<any> = new EventEmitter();

  @Output() menuBlur: EventEmitter<any> = new EventEmitter();

  @Output() menuKeydown: EventEmitter<any> = new EventEmitter();

  @ViewChild('sublist') sublistViewChild: ElementRef;

  constructor(@Inject(DOCUMENT) private readonly document: Document, public el: ElementRef, public renderer: Renderer2, private readonly cd: ChangeDetectorRef, public contextMenu: ContextMenu, private readonly ref: ViewContainerRef) {}

  getItemProp(processedItem: any, name: string, params: any | null = null) {
    return processedItem?.item ? ObjectUtils.getItemValue(processedItem.item[name], params) : undefined;
  }

  getItemId(processedItem: any): string {
    return `${this.menuId}_${processedItem.key}`;
  }

  getItemKey(processedItem: any): string {
    return this.getItemId(processedItem);
  }

  getItemClass(processedItem: any) {
    return {
      ...this.getItemProp(processedItem, 'class'),
      'p-menuitem': true,
      'p-highlight': this.isItemActive(processedItem),
      'p-menuitem-active': this.isItemActive(processedItem),
      'p-focus': this.isItemFocused(processedItem),
      'p-disabled': this.isItemDisabled(processedItem)
    };
  }

  getItemLabel(processedItem: any): string {
    return this.getItemProp(processedItem, 'label');
  }

  getSeparatorItemClass(processedItem: any) {
    return {
      ...this.getItemProp(processedItem, 'class'),
      'p-menuitem-separator': true
    };
  }

  getAriaSetSize() {
    return this.items.filter((processedItem) => this.isItemVisible(processedItem) && !this.getItemProp(processedItem, 'separator')).length;
  }

  getAriaPosInset(index: number) {
    return index - this.items.slice(0, index).filter((processedItem) => this.isItemVisible(processedItem) && this.getItemProp(processedItem, 'separator')).length + 1;
  }

  isItemVisible(processedItem: any): boolean {
    return this.getItemProp(processedItem, 'visible') !== false;
  }

  isItemActive(processedItem: any): boolean {
    if (this.activeItemPath) {
      return this.activeItemPath.some((path) => path.key === processedItem.key);
    }
  }

  isItemDisabled(processedItem: any): boolean {
    return this.getItemProp(processedItem, 'disabled');
  }

  isItemFocused(processedItem: any): boolean {
    return this.focusedItemId === this.getItemId(processedItem);
  }

  isItemGroup(processedItem: any): boolean {
    return ObjectUtils.isNotEmpty(processedItem.items);
  }

  onItemMouseEnter(param: any) {
    const { event, processedItem } = param;
    this.itemMouseEnter.emit({ originalEvent: event, processedItem });
  }

  onItemClick(event: any, processedItem: any) {
    this.getItemProp(processedItem, 'command', { originalEvent: event, item: processedItem.item });
    this.itemClick.emit({ originalEvent: event, processedItem, isFocus: true });
  }

  onEnter(event, sublist) {
    if (event.fromState === 'void' && event.toState) {
      const sublist = event.element;
      this.position(sublist);
    }
  }

  position(sublist) {
    const parentItem = sublist.parentElement.parentElement;
    const containerOffset = DomHandler.getOffset(sublist.parentElement.parentElement);
    const viewport = DomHandler.getViewport();
    const sublistWidth = sublist.offsetParent ? sublist.offsetWidth : DomHandler.getHiddenElementOuterWidth(sublist);
    const itemOuterWidth = DomHandler.getOuterWidth(parentItem.children[0]);

    sublist.style.top = '0px';

    if (parseInt(containerOffset.left, 10) + itemOuterWidth + sublistWidth > viewport.width - DomHandler.calculateScrollbarWidth()) {
      sublist.style.left = -1 * sublistWidth + 'px';
    } else {
      sublist.style.left = itemOuterWidth + 'px';
    }
  }
}
/**
 * ContextMenu displays an overlay menu on right click of its target. Note that components like Table has special integration with ContextMenu.
 * @group Components
 */
@Component({
  selector: 'p-contextMenu',
  template: `
        <div
            #container
            [attr.data-pc-section]="'root'"
            [attr.data-pc-name]="'contextmenu'"
            [id]="id"
            [ngClass]="{ 'p-contextmenu p-component': true, 'p-contextmenu-overlay': true }"
            [class]="styleClass"
            [ngStyle]="style"
            [@overlayAnimation]="{ value: 'visible' }"
            (@overlayAnimation.start)="onOverlayAnimationStart($event)"
            (@overlayAnimation.done)="onOverlayAnimationEnd($event)"
            *ngIf="visible()"
        >
            <p-contextMenuSub
                #rootmenu
                [root]="true"
                [items]="processedItems"
                [menuId]="id"
                [tabindex]="!disabled ? tabindex : -1"
                [ariaLabel]="ariaLabel"
                [ariaLabelledBy]="ariaLabelledBy"
                [baseZIndex]="baseZIndex"
                [autoZIndex]="autoZIndex"
                [visible]="submenuVisible()"
                [focusedItemId]="focused ? focusedItemId : undefined"
                [activeItemPath]="activeItemPath()"
                (itemClick)="onItemClick($event)"
                (menuFocus)="onMenuFocus($event)"
                (menuBlur)="onMenuBlur($event)"
                (menuKeydown)="onKeyDown($event)"
                (itemMouseEnter)="onItemMouseEnter($event)"
            ></p-contextMenuSub>
        </div>
    `,
  animations: [trigger('overlayAnimation', [transition(':enter', [style({ opacity: 0 }), animate('250ms')]), transition(':leave', [animate('.1s linear', style({ opacity: 0 }))])])],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./contextmenu.css'],
  host: {
    class: 'p-element'
  }
})
export class ContextMenu implements OnInit, AfterContentInit, OnDestroy {
  /**
   * An array of menuitems.
   * @group Props
   */
  @Input() model: MenuItem[] | undefined;
  /**
   * Event for which the menu must be displayed.
   * @group Props
   */
  @Input() triggerEvent: string = 'contextmenu';
  /**
   * Local template variable name of the element to attach the context menu.
   * @group Props
   */
  @Input() target: HTMLElement | string | undefined;
  /**
   * Attaches the menu to document instead of a particular item.
   * @group Props
   */
  @Input() global: boolean;
  /**
   * Inline style of the component.
   * @group Props
   */
  @Input() style: { [klass: string]: any } | null | undefined;
  /**
   * Style class of the component.
   * @group Props
   */
  @Input() styleClass: string | undefined;
  /**
   * Target element to attach the overlay, valid values are "body" or a local ng-template variable of another element.
   * @group Props
   */
  @Input() appendTo: HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined | any;
  /**
   * Whether to automatically manage layering.
   * @group Props
   */
  @Input() autoZIndex: boolean = true;
  /**
   * Base zIndex value to use in layering.
   * @group Props
   */
  @Input() baseZIndex: number = 0;
  /**
   * Current id state as a string.
   * @group Props
   */
  @Input() id: string | undefined;
  /**
   * Defines a string value that labels an interactive element.
   * @group Props
   */
  @Input() ariaLabel: string | undefined;
  /**
   * Identifier of the underlying input element.
   * @group Props
   */
  @Input() ariaLabelledBy: string | undefined;
  /**
   * Callback to invoke when overlay menu is shown.
   * @group Emits
   */
  @Output() onShow: EventEmitter<null> = new EventEmitter<null>();
  /**
   * Callback to invoke when overlay menu is hidden.
   * @group Emits
   */
  @Output() onHide: EventEmitter<null> = new EventEmitter<null>();

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate> | undefined;

  @ViewChild('rootmenu') rootmenu: ContextMenuSub | undefined;

  @ViewChild('container') containerViewChild: ElementRef<any> | undefined;

  submenuIconTemplate: Nullable<TemplateRef<any>>;

  container: HTMLDivElement | undefined;

  outsideClickListener: VoidListener;

  resizeListener: VoidListener;

  triggerEventListener: VoidListener;

  documentClickListener: VoidListener;

  documentTriggerListener: VoidListener;

  pageX: number;

  pageY: number;

  visible = signal(false);

  relativeAlign: boolean | undefined;

  private readonly window: Window;

  focused: boolean = false;

  activeItemPath = signal<any>([]);

  focusedItemInfo = signal<any>({ index: -1, level: 0, parentKey: '' });

  submenuVisible = signal<boolean>(false);

  searchValue: string = '';

  searchTimeout: any;

  _processedItems: any[];

  get visibleItems() {
    const processedItem = this.activeItemPath().find((p) => p.key === this.focusedItemInfo().parentKey);

    return processedItem ? processedItem.items : this.processedItems;
  }

  get processedItems() {
    if (!this._processedItems?.length) {
      this._processedItems = this.createProcessedItems(this.model || []);
    }
    return this._processedItems;
  }

  get focusedItemId() {
    const focusedItemInfo = this.focusedItemInfo();
    return focusedItemInfo.index !== -1 ? `${this.id}${ObjectUtils.isNotEmpty(focusedItemInfo.parentKey) ? '_' + focusedItemInfo.parentKey : ''}_${focusedItemInfo.index}` : null;
  }

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    public el: ElementRef,
    public renderer: Renderer2,
    public cd: ChangeDetectorRef,
    public config: PrimeNGConfig,
    public overlayService: OverlayService
  ) {
    this.window = this.document.defaultView as Window;
    effect(() => {
      const path = this.activeItemPath();

      if (ObjectUtils.isNotEmpty(path)) {
        this.bindGlobalListeners();
      } else if (!this.visible()) {
        this.unbindGlobalListeners();
      }
    });
  }

  ngOnInit() {
    this.id = this.id || UniqueComponentId();
    this.bindTriggerEventListener();
  }

  bindTriggerEventListener() {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.triggerEventListener) {
        if (this.global) {
          this.triggerEventListener = this.renderer.listen(this.document, this.triggerEvent, (event) => {
            this.show(event);
          });
        } else if (this.target) {
          this.triggerEventListener = this.renderer.listen(this.target, this.triggerEvent, (event) => {
            this.show(event);
          });
        }
      }
    }
  }

  bindGlobalListeners() {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.documentClickListener) {
        const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

        this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
          if (this.containerViewChild.nativeElement.offsetParent && this.isOutsideClicked(event) && !event.ctrlKey && event.button !== 2 && this.triggerEvent !== 'click') {
            this.hide();
          }
        });

        this.documentTriggerListener = this.renderer.listen(documentTarget, this.triggerEvent, (event) => {
          if (this.containerViewChild.nativeElement.offsetParent && this.isOutsideClicked(event)) {
            this.hide();
          }
        });
      }
      if (!this.resizeListener) {
        this.resizeListener = this.renderer.listen(this.document.defaultView, 'resize', (event) => {
          this.hide();
        });
      }
    }
  }

  ngAfterContentInit() {
    this.templates?.forEach((item) => {
      if(item.getType() == 'submenuicon') {
          this.submenuIconTemplate = item.template;
      }
    });
  }

  createProcessedItems(items: any, level: number = 0, parent: any = {}, parentKey: any = '') {
    const processedItems = [];

    items?.forEach((item, index) => {
      const key = (parentKey !== '' ? parentKey + '_' : '') + index;
      const newItem = {
        item,
        index,
        level,
        key,
        parent,
        parentKey
      };

      newItem['items'] = this.createProcessedItems(item.items, level + 1, newItem, key);
      processedItems.push(newItem);
    });

    return processedItems;
  }

  getItemProp(item: any, name: string) {
    return item ? ObjectUtils.getItemValue(item[name]) : undefined;
  }

  getProccessedItemLabel(processedItem: any) {
    return processedItem ? this.getItemLabel(processedItem.item) : undefined;
  }

  getItemLabel(item: any) {
    return this.getItemProp(item, 'label');
  }

  isProcessedItemGroup(processedItem: any): boolean {
    return processedItem && ObjectUtils.isNotEmpty(processedItem.items);
  }

  isSelected(processedItem: any): boolean {
    return this.activeItemPath().some((p) => p.key === processedItem.key);
  }

  isValidSelectedItem(processedItem: any): boolean {
    return this.isValidItem(processedItem) && this.isSelected(processedItem);
  }

  isValidItem(processedItem: any): boolean {
    return !!processedItem && !this.isItemDisabled(processedItem.item) && !this.isItemSeparator(processedItem.item);
  }

  isItemDisabled(item: any): boolean {
    return this.getItemProp(item, 'disabled');
  }

  isItemSeparator(item: any): boolean {
    return this.getItemProp(item, 'separator');
  }

  isItemMatched(processedItem: any): boolean {
    return this.isValidItem(processedItem) && this.getProccessedItemLabel(processedItem).toLocaleLowerCase().startsWith(this.searchValue.toLocaleLowerCase());
  }

  isProccessedItemGroup(processedItem: any): boolean {
    return processedItem && ObjectUtils.isNotEmpty(processedItem.items);
  }

  onItemClick(event: any) {
    const { processedItem } = event;
    const grouped = this.isProcessedItemGroup(processedItem);
    const selected = this.isSelected(processedItem);

    if (selected) {
      const { index, key, level, parentKey } = processedItem;

      this.activeItemPath.set(this.activeItemPath().filter((p) => key !== p.key && key.startsWith(p.key)));
      this.focusedItemInfo.set({ index, level, parentKey });

      DomHandler.focus(this.rootmenu.sublistViewChild.nativeElement);
    } else {
      grouped ? this.onItemChange(event) : this.hide();
    }
  }

  onItemMouseEnter(event: any) {
    this.onItemChange(event);
  }

  onKeyDown(event: KeyboardEvent) {
    const metaKey = event.metaKey || event.ctrlKey;

    switch (event.code) {
      case 'ArrowDown':
        this.onArrowDownKey(event);
        break;

      case 'ArrowUp':
        this.onArrowUpKey(event);
        break;

      case 'ArrowLeft':
        this.onArrowLeftKey(event);
        break;

      case 'ArrowRight':
        this.onArrowRightKey(event);
        break;

      case 'Home':
        this.onHomeKey(event);
        break;

      case 'End':
        this.onEndKey(event);
        break;

      case 'Space':
        this.onSpaceKey(event);
        break;

      case 'Enter':
        this.onEnterKey(event);
        break;

      case 'Escape':
        this.onEscapeKey(event);
        break;

      case 'Tab':
        this.onTabKey(event);
        break;

      case 'PageDown':
      case 'PageUp':
      case 'Backspace':
      case 'ShiftLeft':
      case 'ShiftRight':
        //NOOP
        break;

      default:
        if (!metaKey && ObjectUtils.isPrintableCharacter(event.key)) {
          this.searchItems(event, event.key);
        }

        break;
    }
  }

  onArrowDownKey(event: KeyboardEvent) {
    const itemIndex = this.focusedItemInfo().index !== -1 ? this.findNextItemIndex(this.focusedItemInfo().index) : this.findFirstFocusedItemIndex();

    this.changeFocusedItemIndex(event, itemIndex);
    event.preventDefault();
    event.stopPropagation();
  }

  onArrowRightKey(event: KeyboardEvent) {
    const processedItem = this.visibleItems[this.focusedItemInfo().index];
    const grouped = this.isProccessedItemGroup(processedItem);

    if (grouped) {
      this.onItemChange({ originalEvent: event, processedItem });
      this.focusedItemInfo.set({ index: -1, parentKey: processedItem.key });
      this.searchValue = '';
      this.onArrowDownKey(event);
    }

    event.preventDefault();
  }

  onArrowUpKey(event: KeyboardEvent) {
    if (event.altKey) {
      if (this.focusedItemInfo().index !== -1) {
        const processedItem = this.visibleItems[this.focusedItemInfo().index];
        const grouped = this.isProccessedItemGroup(processedItem);

        !grouped && this.onItemChange({ originalEvent: event, processedItem });
      }

      this.hide();
      event.preventDefault();
    } else {
      const itemIndex = this.focusedItemInfo().index !== -1 ? this.findPrevItemIndex(this.focusedItemInfo().index) : this.findLastFocusedItemIndex();

      this.changeFocusedItemIndex(event, itemIndex);
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onArrowLeftKey(event: KeyboardEvent) {
    const processedItem = this.visibleItems[this.focusedItemInfo().index];
    const parentItem = this.activeItemPath().find((p) => p.key === processedItem.parentKey);
    const root = ObjectUtils.isEmpty(processedItem.parent);

    if (!root) {
      this.focusedItemInfo.set({ index: -1, parentKey: parentItem ? parentItem.parentKey : '' });
      this.searchValue = '';
      this.onArrowDownKey(event);
    }

    const activeItemPath = this.activeItemPath().filter((p) => p.parentKey !== this.focusedItemInfo().parentKey);
    this.activeItemPath.set(activeItemPath);

    event.preventDefault();
  }

  onHomeKey(event: KeyboardEvent) {
    this.changeFocusedItemIndex(event, this.findFirstItemIndex());
    event.preventDefault();
  }

  onEndKey(event: KeyboardEvent) {
    this.changeFocusedItemIndex(event, this.findLastItemIndex());
    event.preventDefault();
  }

  onSpaceKey(event: KeyboardEvent) {
    this.onEnterKey(event);
  }

  onEscapeKey(event: KeyboardEvent) {
    this.hide();
    this.focusedItemInfo.mutate((value) => {
      value.index = this.findFirstFocusedItemIndex();
    });

    event.preventDefault();
  }

  onTabKey(event: KeyboardEvent) {
    if (this.focusedItemInfo().index !== -1) {
      const processedItem = this.visibleItems[this.focusedItemInfo().index];
      const grouped = this.isProccessedItemGroup(processedItem);

      !grouped && this.onItemChange({ originalEvent: event, processedItem });
    }

    this.hide();
  }

  onEnterKey(event: KeyboardEvent) {
    if (this.focusedItemInfo().index !== -1) {
      const element = DomHandler.findSingle(this.rootmenu.el.nativeElement, `li[id="${`${this.focusedItemId}`}"]`);
      const anchorElement = element && DomHandler.findSingle(element, 'a[data-pc-section="action"]');

      anchorElement ? anchorElement.click() : element?.click();

      const processedItem = this.visibleItems[this.focusedItemInfo().index];
      const grouped = this.isProccessedItemGroup(processedItem);

      if (!grouped) {
        this.focusedItemInfo.mutate((value) => {
          value.index = this.findFirstFocusedItemIndex();
        });
      }
    }

    event.preventDefault();
  }

  onItemChange(event: any) {
    const { processedItem, isFocus } = event;
    if (ObjectUtils.isEmpty(processedItem)) return;

    const { index, key, level, parentKey, items } = processedItem;
    const grouped = ObjectUtils.isNotEmpty(items);
    const activeItemPath = this.activeItemPath().filter((p) => p.parentKey !== parentKey && p.parentKey !== key);

    if (grouped) {
      activeItemPath.push(processedItem);
      this.submenuVisible.set(true);
    }
    this.focusedItemInfo.set({ index, level, parentKey });
    this.activeItemPath.set(activeItemPath);

    isFocus && DomHandler.focus(this.rootmenu.sublistViewChild.nativeElement);
  }

  onMenuFocus(event: any) {
    this.focused = true;
    const focusedItemInfo = this.focusedItemInfo().index !== -1 ? this.focusedItemInfo() : { index: -1, level: 0, parentKey: '' };

    this.focusedItemInfo.set(focusedItemInfo);
    this.onShow.emit();
  }

  onMenuBlur(event: any) {
    this.focused = false;
    this.focusedItemInfo.set({ index: -1, level: 0, parentKey: '' });
    this.searchValue = '';
  }

  onOverlayAnimationStart(event: AnimationEvent) {
    if(event.toState == 'visible') {
      this.container = event.element;
      this.position();
      this.moveOnTop();
      this.appendOverlay();
      this.bindGlobalListeners();
      this.onShow.emit();
      DomHandler.focus(this.rootmenu.sublistViewChild.nativeElement);
    }
  }

  onOverlayAnimationEnd(event: AnimationEvent) {
    if(event.toState == 'void') {
      this.onOverlayHide();
    }
  }

  appendOverlay() {
    if (this.appendTo) {
      if (this.appendTo === 'body') this.renderer.appendChild(this.document.body, this.containerViewChild.nativeElement);
      else DomHandler.appendChild(this.containerViewChild.nativeElement, this.appendTo);
    }
  }

  moveOnTop() {
    if (this.autoZIndex && this.containerViewChild) {
      ZIndexUtils.set('menu', this.containerViewChild.nativeElement, this.baseZIndex + this.config.zIndex.menu);
    }
  }

  onOverlayHide() {
    this.unbindGlobalListeners();

    if (!(this.cd as ViewRef).destroyed) {
      this.target = null;
    }

    // if (this.appendTo) {
    //   this.renderer.appendChild(this.el.nativeElement, this.containerViewChild?.nativeElement);
    // }

    if (this.container && this.autoZIndex) {
      ZIndexUtils.clear(this.container);
    }

    this.container = null;
    this.onHide.emit();
  }

  hide() {
    this.visible.set(false);
    this.activeItemPath.set([]);
    this.focusedItemInfo.set({ index: -1, level: 0, parentKey: '' });
  }

  toggle(event?: any) {
    this.visible() ? this.hide() : this.show(event);
  }

  show(event: any) {
    this.activeItemPath.set([]);
    this.focusedItemInfo.set({ index: -1, level: 0, parentKey: '' });

    this.pageX = event.pageX;
    this.pageY = event.pageY;

    this.visible() ? this.position() : this.visible.set(true);

    event.stopPropagation();
    event.preventDefault();
  }

  position() {
    let left = this.pageX + 1;
    let top = this.pageY + 1;
    let width = this.containerViewChild.nativeElement.offsetParent ? this.containerViewChild.nativeElement.offsetWidth : DomHandler.getHiddenElementOuterWidth(this.containerViewChild.nativeElement);
    let height = this.containerViewChild.nativeElement.offsetParent ? this.containerViewChild.nativeElement.offsetHeight : DomHandler.getHiddenElementOuterHeight(this.containerViewChild.nativeElement);
    let viewport = DomHandler.getViewport();

    //flip
    if (left + width - this.document.scrollingElement.scrollLeft > viewport.width) {
      left -= width;
    }

    //flip
    if (top + height - this.document.scrollingElement.scrollTop > viewport.height) {
      top -= height;
    }

    //fit
    if (left < this.document.scrollingElement.scrollLeft) {
      left = this.document.scrollingElement.scrollLeft;
    }

    //fit
    if (top < this.document.scrollingElement.scrollTop) {
      top = this.document.scrollingElement.scrollTop;
    }

    this.containerViewChild.nativeElement.style.left = left + 'px';
    this.containerViewChild.nativeElement.style.top = top + 'px';
  }

  searchItems(event: any, char: string) {
    this.searchValue = (this.searchValue || '') + char;

    let itemIndex = -1;
    let matched = false;

    if (this.focusedItemInfo().index !== -1) {
      itemIndex = this.visibleItems.slice(this.focusedItemInfo().index).findIndex((processedItem) => this.isItemMatched(processedItem));
      itemIndex = itemIndex === -1 ? this.visibleItems.slice(0, this.focusedItemInfo().index).findIndex((processedItem) => this.isItemMatched(processedItem)) : itemIndex + this.focusedItemInfo().index;
    } else {
      itemIndex = this.visibleItems.findIndex((processedItem) => this.isItemMatched(processedItem));
    }

    if (itemIndex !== -1) {
      matched = true;
    }

    if (itemIndex === -1 && this.focusedItemInfo().index === -1) {
      itemIndex = this.findFirstFocusedItemIndex();
    }

    if (itemIndex !== -1) {
      this.changeFocusedItemIndex(event, itemIndex);
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.searchValue = '';
      this.searchTimeout = null;
    }, 500);

    return matched;
  }

  findLastFocusedItemIndex() {
    const selectedIndex = this.findSelectedItemIndex();
    return selectedIndex < 0 ? this.findLastItemIndex() : selectedIndex;
  }

  findLastItemIndex() {
    return ObjectUtils.findLastIndex(this.visibleItems, (processedItem) => this.isValidItem(processedItem));
  }

  findPrevItemIndex(index: number) {
    const matchedItemIndex = index > 0 ? ObjectUtils.findLastIndex(this.visibleItems.slice(0, index), (processedItem) => this.isValidItem(processedItem)) : -1;

    return matchedItemIndex > -1 ? matchedItemIndex : index;
  }

  findNextItemIndex(index: number) {
    const matchedItemIndex = index < this.visibleItems.length - 1 ? this.visibleItems.slice(index + 1).findIndex((processedItem) => this.isValidItem(processedItem)) : -1;

    return matchedItemIndex > -1 ? matchedItemIndex + index + 1 : index;
  }

  findFirstFocusedItemIndex() {
    const selectedIndex = this.findSelectedItemIndex();

    return selectedIndex < 0 ? this.findFirstItemIndex() : selectedIndex;
  }

  findFirstItemIndex() {
    return this.visibleItems.findIndex((processedItem) => this.isValidItem(processedItem));
  }

  findSelectedItemIndex() {
    return this.visibleItems.findIndex((processedItem) => this.isValidSelectedItem(processedItem));
  }

  changeFocusedItemIndex(event: any, index: number) {
    if (this.focusedItemInfo().index !== index) {
      this.focusedItemInfo.mutate((value) => {
        value.index = index;
      });
      this.scrollInView();
    }
  }

  scrollInView(index: number = -1) {
    const id = index !== -1 ? `${this.id}_${index}` : this.focusedItemId;
    const element = DomHandler.findSingle(this.rootmenu.el.nativeElement, `li[id="${id}"]`);

    if (element) {
      element.scrollIntoView({ block: 'nearest', inline: 'start' });
    }
  }

  bindResizeListener() {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.resizeListener) {
        this.resizeListener = this.renderer.listen(this.document.defaultView, 'resize', (event) => {
          this.hide();
        });
      }
    }
  }

  isOutsideClicked(event: Event) {
    return !(this.containerViewChild.nativeElement.isSameNode(event.target) || this.containerViewChild.nativeElement.contains(event.target));
  }

  unbindResizeListener() {
    if (this.resizeListener) {
      this.resizeListener();
      this.resizeListener = null;
    }
  }

  unbindGlobalListeners() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }

    if (this.documentTriggerListener) {
      this.documentTriggerListener();
      this.documentTriggerListener = null;
    }

    if (this.resizeListener) {
      this.resizeListener();
      this.resizeListener = null;
    }
  }

  unbindTriggerEventListener() {
    if (this.triggerEventListener) {
      this.triggerEventListener();
      this.triggerEventListener = null;
    }
  }

  ngOnDestroy() {
    if (document.getElementById(this.id))
      document.getElementById(this.id).remove()
    this.unbindGlobalListeners();
    this.unbindTriggerEventListener();
  }
}

@NgModule({
  imports: [CommonModule, RouterModule, RippleModule, TooltipModule, AngleRightIcon, SharedModule],
  exports: [ContextMenu, RouterModule, TooltipModule, SharedModule],
  declarations: [ContextMenu, ContextMenuSub]
})
export class ContextMenuModule {}
